import React, { useState, useEffect, useCallback } from "react";
import Dropdown from "react-dropdown";
import "react-dropdown/style.css";
import "./editUser.scss";
import Modal from "../modal/modal";
import { COUNTRIES } from "../../consts";
import deleteUserIcon from "./delete-user-icon.svg";
import deleteIcon from "./delete-icon.svg";
import ApiHandler from "../api";

const EditUser = ({ user, closeEditUser, deleteUser, onUserUpdated }) => {
  const [projects, setProjects] = useState([]);
  const [fullName, setFullName] = useState(user?.fullName || "");
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || "");
  const [email, setEmail] = useState(user?.email || "");
  const [role, setRole] = useState(user?.role || "");
  const [userProjects, setUserProjects] = useState(user?.projects || []);
  const [projectGroups, setProjectGroups] = useState([]);
  const [userProjectGroups, setUserProjectGroups] = useState(
    user?.projectGroups || []
  );
  const [countryAdminCodes, setCountryAdminCodes] = useState(
    user?.countryAdminCodes || []
  );
  const [robotWhitelists, setRobotWhitelists] = useState(
    user?.robotWhitelists || {}
  );

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const apiHandler = new ApiHandler();

  useEffect(() => {
    if (user) {
      setFullName(user.fullName || "");
      setPhoneNumber(user.phoneNumber || "");
      setEmail(user.email || "");
      setRole(user.role || "");
      setUserProjects(user.projects || []);
      setUserProjectGroups(user.projectGroups || []);
      setCountryAdminCodes(user.countryAdminCodes || []);
      setRobotWhitelists(user.robotWhitelists || {});
    }
  }, [user]);

  const extractProjectsFromGroups = useCallback((projectGroups) => {
    if (!projectGroups || !Array.isArray(projectGroups)) return [];

    const allProjects = [];

    const extractFromGroup = (group) => {
      // Add projects from current group
      if (group.projects && Array.isArray(group.projects)) {
        allProjects.push(...group.projects);
      }

      // Recursively extract from child groups
      if (group.children && Array.isArray(group.children)) {
        group.children.forEach((child) => extractFromGroup(child));
      }
    };

    projectGroups.forEach((group) => extractFromGroup(group));

    // Remove duplicates based on _id and sort by projectName
    const uniqueProjects = allProjects.filter(
      (project, index, self) =>
        index === self.findIndex((p) => p._id === project._id)
    );

    return uniqueProjects.sort((a, b) =>
      a.projectName.localeCompare(b.projectName)
    );
  }, []);

  const handleProjects = async () => {
    const result = await apiHandler.getProjects();
    if (result.projectGroups) {
      setProjectGroups(result.projectGroups);
      const extractedProjects = extractProjectsFromGroups(result.projectGroups);
      setProjects(extractedProjects);
    }
  };

  useEffect(() => {
    handleProjects();
  }, []);

  useEffect(() => {
    if (error) {
      console.log(error);
      let text = Object.values(error.response?.data || {}).join(", ");
      alert(text);
      setError(null);
    }
  }, [error]);

  const onChange = useCallback((value, param) => {
    switch (param) {
      case "fullName":
        setFullName(value);
        break;
      case "phoneNumber":
        setPhoneNumber(value);
        break;
      case "email":
        setEmail(value);
        break;
      default:
        break;
    }
  }, []);

  const onSubmit = useCallback(async () => {
    let missing = [];

    if (!fullName) missing.push("full name");
    if (!phoneNumber) missing.push("phone number");
    if (!role) missing.push("role");
    if (!email) missing.push("email");

    if (missing.length) {
      alert(
        `The following ${
          missing.length > 1 ? "fields are" : "field is"
        } mandatory: ${missing.join(", ")}`
      );
      return;
    }

    // Validate robot whitelist data if present
    if (robotWhitelists) {
      for (const [projectId, whitelist] of Object.entries(robotWhitelists)) {
        const validation = validateRobotWhitelist(whitelist);
        if (!validation.isValid) {
          alert(
            `Invalid robot whitelist format for project ${projectId}: ${validation.message}`
          );
          return;
        }
      }
    }

    setIsLoading(true);
    try {
      let params = {
        fullName,
        phoneNumber,
        email,
        role,
        projects: userProjects,
        projectGroups: userProjectGroups,
        countryAdminCodes,
        robotWhitelists,
      };
      if (params.projects) params.projects = params.projects.map((p) => p.id);
      if (params.projectGroups)
        params.projectGroups = params.projectGroups.map((p) => p.id);

      let updatedUser;
      if (user) {
        updatedUser = await apiHandler.editUser(user.id, params);
      } else {
        updatedUser = await apiHandler.addUser(params);
      }

      // Notify parent component about the update
      if (onUserUpdated) {
        onUserUpdated(updatedUser);
      }

      closeEditUser();
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [
    fullName,
    phoneNumber,
    email,
    role,
    userProjects,
    userProjectGroups,
    countryAdminCodes,
    robotWhitelists,
    user,
    apiHandler,
    onUserUpdated,
    closeEditUser,
  ]);

  const onRoleSelected = useCallback((item) => {
    setRole(item.value);
  }, []);

  const getTitle = useCallback(() => {
    if (user) return "Edit User";
    else return "Add User";
  }, [user]);

  const onAddCountry = useCallback(
    (item) => {
      if (countryAdminCodes.includes(item.value)) return;
      setCountryAdminCodes([...countryAdminCodes, item.value]);
    },
    [countryAdminCodes]
  );

  const removeCountry = useCallback(
    (country) => {
      const newCountryAdminCodes = countryAdminCodes.filter(
        (c) => c !== country
      );
      setCountryAdminCodes(newCountryAdminCodes);
    },
    [countryAdminCodes]
  );

  const onAddProject = useCallback(
    (item) => {
      if (userProjects.some((p) => p.id === item.value)) return;
      setUserProjects([...userProjects, { id: item.value, name: item.label }]);
    },
    [userProjects]
  );

  const onAddProjectGroup = useCallback(
    (item) => {
      if (userProjectGroups.some((p) => p.id === item.value)) return;
      setUserProjectGroups([
        ...userProjectGroups,
        { id: item.value, name: item.label },
      ]);
    },
    [userProjectGroups]
  );

  const removeProject = useCallback(
    (projectId) => {
      const newProjects = userProjects.filter((p) => p.id !== projectId);
      setUserProjects(newProjects);
    },
    [userProjects]
  );

  const removeProjectGroup = useCallback(
    (projectGroupId) => {
      const newProjectGroups = userProjectGroups.filter(
        (p) => p.id !== projectGroupId
      );
      setUserProjectGroups(newProjectGroups);
    },
    [userProjectGroups]
  );

  const updateRobotWhitelist = useCallback((projectId, value) => {
    setRobotWhitelists((prev) => ({
      ...prev,
      [projectId]: value,
    }));
  }, []);

  const onRobotWhitelistChange = useCallback(
    (projectId, value) => {
      updateRobotWhitelist(projectId, value);

      // Provide real-time validation feedback
      if (value && value.trim() !== "") {
        const validation = validateRobotWhitelist(value);
        if (!validation.isValid) {
          // You could show a tooltip or inline error message here
          console.warn(`Invalid robot whitelist format: ${validation.message}`);
        }
      }
    },
    [updateRobotWhitelist]
  );

  const validateRobotWhitelist = useCallback((whitelist) => {
    if (!whitelist || whitelist.trim() === "") {
      return { isValid: true, message: "" };
    }

    // Basic validation for robot whitelist format
    const parts = whitelist.split(",").map((part) => part.trim());

    for (const part of parts) {
      if (part.includes("-")) {
        // Handle range (e.g., "1-5")
        const [start, end] = part.split("-").map((num) => parseInt(num.trim()));

        if (isNaN(start) || isNaN(end) || start > end) {
          return {
            isValid: false,
            message: `Invalid range format: ${part}. Use format like "1-5"`,
          };
        }
      } else {
        // Handle single number
        const robotNumber = parseInt(part);
        if (isNaN(robotNumber) || robotNumber < 1) {
          return {
            isValid: false,
            message: `Invalid robot number: ${part}. Use positive numbers only`,
          };
        }
      }
    }

    return { isValid: true, message: "" };
  }, []);

  const saveRobotWhitelist = useCallback(
    async (projectId, whitelist) => {
      if (!user || !user._id) return;

      // Validate the whitelist format
      const validation = validateRobotWhitelist(whitelist);
      if (!validation.isValid) {
        alert(`Invalid robot whitelist format: ${validation.message}`);
        return;
      }

      try {
        await apiHandler.setRobotWhitelist(user._id, projectId, whitelist);

        // Update the local state to reflect the saved value
        setRobotWhitelists((prev) => ({
          ...prev,
          [projectId]: whitelist,
        }));
      } catch (error) {
        console.error("Error saving robot whitelist:", error);
        alert("Error saving robot whitelist. Please try again.");
      }
    },
    [user, apiHandler, validateRobotWhitelist]
  );

  const refreshRobotWhitelists = useCallback(async () => {
    if (!user || !user._id) return;

    try {
      const whitelistsData = await apiHandler.getUserRobotWhitelists(user._id);
      const whitelistMap = {};
      whitelistsData.forEach((whitelist) => {
        whitelistMap[whitelist.projectId] = whitelist.whitelist;
      });
      setRobotWhitelists(whitelistMap);
    } catch (error) {
      console.error("Error refreshing robot whitelists:", error);
    }
  }, [user, apiHandler]);

  return (
    <Modal className="edit-user" title={getTitle()} onClose={closeEditUser}>
      <div className="input-line">
        <div className="input-description">Full Name</div>
        <input
          value={fullName}
          placeholder=""
          type="text"
          onChange={(e) => onChange(e.target.value, "fullName")}
        />
      </div>
      <div className="input-line">
        <div className="input-description">Phone Number</div>
        <input
          value={phoneNumber}
          placeholder=""
          type="tel"
          onChange={(e) => onChange(e.target.value, "phoneNumber")}
        />
      </div>
      <div className="input-line">
        <div className="input-description">Email</div>
        <input
          value={email}
          placeholder=""
          type="email"
          onChange={(e) => onChange(e.target.value, "email")}
        />
      </div>
      <div className="input-line">
        <div className="input-description">Role</div>
        <Dropdown
          options={[
            { label: "Plant owner", value: "projectOwner" },
            { label: "Operator", value: "operator" },
            { label: "Technician", value: "technician" },
            { label: "Country Admin", value: "countryAdmin" },
            { label: "Admin", value: "admin" },
          ]}
          onChange={onRoleSelected}
          value={role}
          className="dropdown"
          controlClassName="control"
        />
      </div>

      {role === "countryAdmin" ? (
        <div className="input-line">
          <div className="input-description">Countries</div>
          <Dropdown
            options={COUNTRIES}
            onChange={onAddCountry}
            placeholder="Add country"
            className="dropdown"
            controlClassName="control"
          />
          <div className="countries">
            {countryAdminCodes.map((country) => (
              <li key={country} className="country">
                <span className="text">{country}</span>
                <span className="remove" onClick={() => removeCountry(country)}>
                  <img src={deleteIcon} />
                </span>
              </li>
            ))}
          </div>
        </div>
      ) : null}

      {role === "operator" ||
      role === "technician" ||
      role === "projectOwner" ? (
        <div>
          <div className="input-line">
            <div className="input-description">Projects</div>
            <Dropdown
              options={projects.map((p) => ({
                label: p.projectName,
                value: p._id,
              }))}
              onChange={onAddProject}
              placeholder="Add project"
              className="dropdown"
              controlClassName="control"
            />
            <div className="projects">
              {userProjects.map((project) => (
                <div key={project.id} className="project-container">
                  <li className="project">
                    <span className="text">{project.name}</span>
                    <span
                      className="remove"
                      onClick={() => removeProject(project.id)}
                    >
                      <img src={deleteIcon} />
                    </span>
                  </li>
                  {role === "technician" && (
                    <div className="robot-whitelist-input">
                      <input
                        type="text"
                        value={robotWhitelists[project.id] || ""}
                        placeholder="Allowed robot numbers (e.g., 1,2,3 or 1-5)"
                        onChange={(e) =>
                          onRobotWhitelistChange(project.id, e.target.value)
                        }
                        onBlur={(e) =>
                          saveRobotWhitelist(project.id, e.target.value)
                        }
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="input-line">
            <div className="input-description">Project Groups</div>
            <Dropdown
              options={projectGroups.map((pg) => ({
                label: pg.projectGroup?.name || pg.name,
                value: pg.projectGroup?._id || pg._id,
              }))}
              onChange={onAddProjectGroup}
              placeholder="Add project group"
              className="dropdown"
              controlClassName="control"
            />
            <div className="projects">
              {userProjectGroups.map((projectGroup) => (
                <li key={projectGroup.id} className="project">
                  <span className="text">{projectGroup.name}</span>
                  <span
                    className="remove"
                    onClick={() => removeProjectGroup(projectGroup.id)}
                  >
                    <img src={deleteIcon} />
                  </span>
                </li>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {user ? (
        <div className="buttons-wrapper">
          <div className="delete-row" onClick={deleteUser}>
            <img src={deleteUserIcon} />
            <span>Delete User</span>
          </div>
          <div className="button cancel" onClick={closeEditUser}>
            CANCEL
          </div>
          <div className="button" onClick={onSubmit} disabled={isLoading}>
            {isLoading ? "SAVING..." : "SAVE"}
          </div>
        </div>
      ) : (
        <div className="button" onClick={onSubmit} disabled={isLoading}>
          {isLoading ? "SAVING..." : "SAVE"}
        </div>
      )}
    </Modal>
  );
};

export default EditUser;
